import {
  Button as WeaveButton,
  ButtonSizes as WeaveButtonSizes,
  ButtonVariants as WeaveButtonVariants,
  Slider as WeaveSlider,
} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useMemo} from 'react';

import {MaxRunsWarningBanner} from '../../MaxRuns/MaxRunsWarningBanner';
import {useMaxRunsWarningBanner} from '../../MaxRuns/useMaxRunsWarningBanner';
import {useInheritedSettings} from '../../Panel/hooks/useInheritedSettings';
import {SettingLabel} from '../../WorkspaceDrawer/Settings/components/SettingLabel';
import {type useTrackSettingsChange} from '../../WorkspaceDrawer/Settings/hooks/useTrackSettingEvent';
import {usePanelConfigContext} from '../PanelConfigContext';
import {type RunsLinePlotConfig} from '../types';

export const EditMaxRuns = ({
  config,
  isGrouped,
  settingsToTrack,
  setRunOnApply,
  trackPanelSettingsChange,
  updateConfig,
}: {
  config: RunsLinePlotConfig;
  isGrouped: boolean;
  settingsToTrack: Record<string, any>;
  setRunOnApply: (runOnApply: () => void) => void;
  trackPanelSettingsChange: ReturnType<typeof useTrackSettingsChange>;
  updateConfig: (config: RunsLinePlotConfig) => void;
}) => {
  const inheritedSettings = useInheritedSettings();
  const {limit, maxRunsLimit, isFullFidelity} = usePanelConfigContext();
  const showWarningBanner = useMaxRunsWarningBanner({
    isFullFidelity,
    numRuns: limit,
  });

  const sliderValue = useMemo(() => [limit], [limit]);

  const handleResetLimit = useCallback(() => {
    // oxlint-disable-next-line react/immutability
    settingsToTrack.current = {
      ...settingsToTrack.current,
      numberOfRuns: undefined,
    };
    setRunOnApply(() => () => {
      trackPanelSettingsChange({
        setting: 'numberOfRuns',
        settingValue: settingsToTrack.current?.numberOfRuns?.toString(),
      });
    });
    updateConfig({
      limit: undefined,
    });
  }, [setRunOnApply, settingsToTrack, trackPanelSettingsChange, updateConfig]);

  const handleSliderValueChange = useCallback(
    (newVal: number[]) => {
      // oxlint-disable-next-line react/immutability
      settingsToTrack.current = {
        ...settingsToTrack.current,
        limit: newVal[0],
      };
      setRunOnApply(() => () => {
        trackPanelSettingsChange({
          setting: 'limit',
          settingValue: settingsToTrack.current?.limit?.toString(),
        });
      });
      updateConfig({limit: newVal[0]});
    },
    [setRunOnApply, settingsToTrack, trackPanelSettingsChange, updateConfig]
  );

  const handleDisplayChange = useCallback(
    (newVal: number) => {
      let nextVal = newVal;
      if (nextVal < 1) {
        nextVal = 1;
      }
      if (nextVal > maxRunsLimit) {
        nextVal = maxRunsLimit;
      }

      // oxlint-disable-next-line react/immutability
      settingsToTrack.current = {
        ...settingsToTrack.current,
        numberOfRuns: nextVal,
      };
      setRunOnApply(() => () => {
        trackPanelSettingsChange({
          setting: 'numberOfRuns',
          settingValue: settingsToTrack.current?.numberOfRuns?.toString(),
        });
      });
      updateConfig({
        limit: nextVal,
      });
    },
    [
      maxRunsLimit,
      setRunOnApply,
      settingsToTrack,
      trackPanelSettingsChange,
      updateConfig,
    ]
  );

  return (
    <Tailwind>
      <div
        className="flex items-center justify-between"
        data-test="edit-data-max-runs">
        <SettingLabel
          htmlFor="max-runs"
          text={`Max ${isGrouped ? 'group' : 'run'}s to show`}
          className="w-full"
        />
        <WeaveButton
          onClick={handleResetLimit}
          size={WeaveButtonSizes.Medium}
          icon="randomize-reset-reload"
          aria-label="Reset"
          tooltip="Reset"
          variant={WeaveButtonVariants.Ghost}
        />
      </div>
      <div className={'mt-4 grid  grid-cols-[1fr_auto] items-center gap-12'}>
        <WeaveSlider.Root
          name="max-runs"
          onValueChange={handleSliderValueChange}
          step={1}
          min={1}
          max={maxRunsLimit}
          value={sliderValue}>
          <WeaveSlider.Track>
            <WeaveSlider.Range />
          </WeaveSlider.Track>
          <WeaveSlider.Thumb />
        </WeaveSlider.Root>
        <div data-test="max-runs-display">
          <WeaveSlider.Display
            // TODO: think about updating the API here
            isDirty={
              config.limit != null &&
              config.limit !== inheritedSettings?.linePlot?.limit
            }
            onChange={handleDisplayChange}
            step={1}
            min={1}
            max={maxRunsLimit}
            value={limit}
          />
        </div>
      </div>
      {showWarningBanner && (
        <MaxRunsWarningBanner location="panel-config" numRuns={limit} />
      )}
    </Tailwind>
  );
};
